import React, { useState, useEffect } from 'react';
import '../styles/SelectGroupGame.css';

const SelectGroupGame = ({ gameData, onComplete, isLastGame, onSubmitFinal, previousAnswers = null }) => {
  const [groups, setGroups] = useState([]);
  const [availableWords, setAvailableWords] = useState([]);
  const [placedWords, setPlacedWords] = useState({});
  const [draggedWord, setDraggedWord] = useState(null);
  const [dragSourceGroup, setDragSourceGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stati per la modalità clic su mobile
  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedWordSource, setSelectedWordSource] = useState(null);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  
  // Chiave unica per il localStorage basata sul chatId
  const getLocalStorageKey = () => {
    return gameData && gameData.chatId ? `selectgroup_partial_answers_${gameData.chatId}` : null;
  };
  
  // Funzione migliorata per la gestione delle chiavi localStorage
  const getStorageKeys = () => {
    const primaryKey = getLocalStorageKey();
    const metaKey = primaryKey ? `${primaryKey}_meta` : null;
    return { primaryKey, metaKey };
  };
  
  // Funzione per pulire i dati dal localStorage
  const clearLocalStorageData = () => {
    const { primaryKey, metaKey } = getStorageKeys();
    if (primaryKey) {
      try {
        localStorage.removeItem(primaryKey);
        localStorage.removeItem(metaKey);
        console.log(`SelectGroupGame [${gameData?.chatId}] - Dati parziali rimossi da localStorage`);
      } catch (e) {
        console.error(`SelectGroupGame [${gameData?.chatId}] - Errore nella rimozione dei dati da localStorage:`, e);
      }
    }
  };
  
  // Controlla e pulisci i dati scaduti all'avvio del componente
  useEffect(() => {
    // Pulisce tutti i dati SelectGroup scaduti nel localStorage
    const cleanupExpiredSelectGroupData = () => {
      try {
        const now = Date.now();
        // Cerca tutte le chiavi localStorage relative a SelectGroup
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('selectgroup_partial_answers_') && key.includes('_meta')) {
            try {
              const meta = JSON.parse(localStorage.getItem(key));
              // Se i dati sono scaduti (più vecchi di 1 ora) o è presente una flag di completamento
              if (meta && (now - meta.timestamp > 3600000 || meta.completed)) {
                const baseKey = key.replace('_meta', '');
                localStorage.removeItem(baseKey);
                localStorage.removeItem(key);
                console.log(`Rimozione automatica di dati scaduti: ${baseKey}`);
              }
            } catch (parseError) {
              // Se c'è un errore nel parsing, rimuovi comunque
              const baseKey = key.replace('_meta', '');
              localStorage.removeItem(baseKey);
              localStorage.removeItem(key);
            }
          }
        });
      } catch (error) {
        console.error('Errore durante la pulizia dei dati scaduti:', error);
      }
    };
    
    // Esegue la pulizia dei dati scaduti
    cleanupExpiredSelectGroupData();
    
    // Pulizia quando il componente viene smontato
    return () => {
      console.log(`SelectGroupGame [${gameData?.chatId}] - Componente smontato, avvio pulizia`);
      clearLocalStorageData();
    };
  }, [gameData?.chatId]);
  
  // Setup game data e caricamento stato precedente
  useEffect(() => {
    if (gameData && gameData.selectgroup) {
      setGroups(gameData.selectgroup || []);
      
      // Raccoglie tutte le parole
      const allWords = [];
      gameData.selectgroup.forEach(group => {
        group.words.forEach(word => {
          allWords.push(word);
        });
      });
      
      // Inizializza placedWords con gruppi vuoti
      const initialPlaced = {};
      gameData.selectgroup.forEach(group => {
        initialPlaced[group.id] = [];
      });
      
      // Chiave per il localStorage
      const { primaryKey, metaKey } = getStorageKeys();
      let loadedPlacedWords = {}; 
      let wordsToPlace = [...allWords];
      
      // Flag per decidere se caricare i dati dal localStorage
      let shouldLoadFromLocalStorage = true;
      
      // Se abbiamo dati nel localStorage, controlla la scadenza
      if (primaryKey && metaKey) {
        try {
          const metaData = localStorage.getItem(metaKey);
          if (metaData) {
            const meta = JSON.parse(metaData);
            const now = Date.now();
            // Verifica se i dati sono scaduti (più vecchi di 1 ora)
            if (now - meta.timestamp > 3600000) {
              // Dati scaduti, rimuovili
              localStorage.removeItem(primaryKey);
              localStorage.removeItem(metaKey);
              shouldLoadFromLocalStorage = false;
              console.log(`SelectGroupGame [${gameData.chatId}] - Dati scaduti rimossi (${(now - meta.timestamp)/1000}s)`);
            } else if (meta.completed) {
              // Se i dati erano già marcati come completati, non caricarli
              shouldLoadFromLocalStorage = false;
              console.log(`SelectGroupGame [${gameData.chatId}] - Ignoro dati di gioco già completato`);
            }
          }
        } catch (e) {
          console.error(`SelectGroupGame [${gameData.chatId}] - Errore controllo metadati:`, e);
          shouldLoadFromLocalStorage = false;
        }
      }
      
      // Priorità 1: Controlla se ci sono dati salvati in localStorage
      if (primaryKey && shouldLoadFromLocalStorage) {
        try {
          const savedData = localStorage.getItem(primaryKey);
          if (savedData) {
            loadedPlacedWords = JSON.parse(savedData);
            console.log(`SelectGroupGame [${gameData.chatId}] - Parole posizionate caricate da localStorage`);
            
            // Rimuoviamo le parole già posizionate dalla lista di parole disponibili
            Object.values(loadedPlacedWords).forEach(groupWords => {
              groupWords.forEach(word => {
                wordsToPlace = wordsToPlace.filter(w => w !== word);
              });
            });
          }
        } catch (e) {
          console.error(`SelectGroupGame [${gameData.chatId}] - Errore nel caricamento delle parole da localStorage:`, e);
        }
      }
      
      // Priorità 2: Se non ci sono dati in localStorage ma ci sono risultati precedenti
      if (Object.keys(loadedPlacedWords).length === 0 && previousAnswers && previousAnswers.groupResults) {
        console.log(`SelectGroupGame [${gameData.chatId}] - Carico dati da risultati precedenti`);
        
        // Ricostruiamo lo stato delle parole posizionate dai risultati precedenti
        previousAnswers.groupResults.forEach(groupResult => {
          const groupId = groupResult.groupId;
          loadedPlacedWords[groupId] = [...groupResult.userSelection];
          
          // Rimuoviamo le parole già posizionate dalla lista di parole disponibili
          groupResult.userSelection.forEach(word => {
            wordsToPlace = wordsToPlace.filter(w => w !== word);
          });
        });
      }
      
      // Se abbiamo caricato parole posizionate, le utilizziamo
      if (Object.keys(loadedPlacedWords).length > 0) {
        setPlacedWords(loadedPlacedWords);
      } else {
        setPlacedWords(initialPlaced);
      }
      
      // Mischia le parole rimanenti
      const shuffledWords = shuffleArray(wordsToPlace);
      setAvailableWords(shuffledWords);
    }
  }, [gameData, previousAnswers]);
  
  // Salva le parole posizionate in localStorage ogni volta che vengono aggiornate
  useEffect(() => {
    const { primaryKey, metaKey } = getStorageKeys();
    if (primaryKey && metaKey && gameData && Object.keys(placedWords).length > 0) {
      try {
        // Salva i dati principali
        localStorage.setItem(primaryKey, JSON.stringify(placedWords));
        
        // Salva i metadati con timestamp
        const meta = {
          timestamp: Date.now(),
          chatId: gameData.chatId,
          completed: false // Questo verrà aggiornato a true quando il gioco sarà completato
        };
        localStorage.setItem(metaKey, JSON.stringify(meta));
        
        console.log(`SelectGroupGame [${gameData.chatId}] - Dati salvati in localStorage`);
      } catch (e) {
        console.error(`SelectGroupGame [${gameData.chatId}] - Errore nel salvataggio in localStorage:`, e);
      }
    }
  }, [placedWords, gameData]);
  
  // Funzione per mischiare un array
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };
  
  // Gestione del drag di una parola
  const handleDragStart = (e, word) => {
    setDraggedWord(word);
    setDragSourceGroup(null);
    e.dataTransfer.setData('text/plain', word);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Gestione del drag di una parola già posizionata
  const handleDragStartFromGroup = (e, groupId, word) => {
    setDraggedWord(word);
    setDragSourceGroup(groupId);
    e.dataTransfer.setData('text/plain', word);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Fine del drag
  const handleDragEnd = () => {
    setDraggedWord(null);
    setDragSourceGroup(null);
  };
  
  // Permette il drop
  const handleDragOver = (e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  // Gestione del drop in un gruppo
  const handleDrop = (e, groupId) => {
    e.preventDefault();
    
    if (!draggedWord) return;
    
    // Se la parola viene dall'area disponibile
    if (dragSourceGroup === null) {
      setAvailableWords(prev => prev.filter(w => w !== draggedWord));
      setPlacedWords(prev => ({
        ...prev,
        [groupId]: [...prev[groupId], draggedWord]
      }));
    } 
    // Se la parola viene da un altro gruppo
    else if (dragSourceGroup !== null) {
      setPlacedWords(prev => ({
        ...prev,
        [dragSourceGroup]: prev[dragSourceGroup].filter(w => w !== draggedWord),
        [groupId]: [...prev[groupId], draggedWord]
      }));
    }
    
    setDraggedWord(null);
    setDragSourceGroup(null);
  };
  
  // Gestione del drop nell'area disponibile
  const handleDropToAvailable = (e) => {
    e.preventDefault();
    
    if (!draggedWord || dragSourceGroup === null) return;
    
    // Rimuovi la parola dal gruppo di origine e aggiungila all'area disponibile
    setPlacedWords(prev => ({
      ...prev,
      [dragSourceGroup]: prev[dragSourceGroup].filter(w => w !== draggedWord)
    }));
    setAvailableWords(prev => [...prev, draggedWord]);
    
    setDraggedWord(null);
    setDragSourceGroup(null);
  };
  
  // Rimuovi una parola da un gruppo (cliccando)
  const handleRemoveWord = (groupId, word) => {
    setPlacedWords(prev => ({
      ...prev,
      [groupId]: prev[groupId].filter(w => w !== word)
    }));
    setAvailableWords(prev => [...prev, word]);
  };
  
  // Funzione per gestire il clic su una parola nell'area disponibile
  const handleWordClick = (word) => {
    setSelectedWord(word);
    setSelectedWordSource(null);
    setShowGroupSelector(true);
  };

  // Funzione per gestire il clic su una parola già in un gruppo
  const handlePlacedWordClick = (e, groupId, word) => {
    // Previene il comportamento di rimozione della parola quando si vuole selezionare
    e.stopPropagation();
    
    // Se c'è già una 'x' visibile per rimuovere, usiamo quel comportamento
    if (e.target.className.includes('selectgroup-remove-icon')) {
      handleRemoveWord(groupId, word);
      return;
    }
    
    setSelectedWord(word);
    setSelectedWordSource(groupId);
    setShowGroupSelector(true);
  };

  // Funzione per assegnare la parola selezionata a un gruppo
  const handleAssignToGroup = (groupId) => {
    if (!selectedWord) return;
    
    // Se la parola proviene dall'area disponibile
    if (selectedWordSource === null) {
      setAvailableWords(prev => prev.filter(w => w !== selectedWord));
      setPlacedWords(prev => ({
        ...prev,
        [groupId]: [...prev[groupId], selectedWord]
      }));
    } 
    // Se la parola proviene da un altro gruppo
    else {
      setPlacedWords(prev => ({
        ...prev,
        [selectedWordSource]: prev[selectedWordSource].filter(w => w !== selectedWord),
        [groupId]: [...prev[groupId], selectedWord]
      }));
    }
    
    // Reset degli stati di selezione
    setSelectedWord(null);
    setSelectedWordSource(null);
    setShowGroupSelector(false);
  };

  // Funzione per annullare la selezione
  const handleCancelSelection = () => {
    setSelectedWord(null);
    setSelectedWordSource(null);
    setShowGroupSelector(false);
  };
  
  // Funzione per calcolare i risultati senza mostrare la schermata dei risultati
  const calculateResults = () => {
    // Crea una mappa che associa ogni parola al suo gruppo corretto
    const correctGroupMap = {};
    let totalWordsInGroups = 0;
    
    groups.forEach(group => {
      group.words.forEach(word => {
        correctGroupMap[word] = group.id;
        totalWordsInGroups++; // Contiamo il numero totale di parole nei gruppi
      });
    });
    
    // Calcola il punteggio
    let correctCount = 0;
    let totalPlacedWords = 0;
    
    Object.entries(placedWords).forEach(([groupId, words]) => {
      words.forEach(word => {
        totalPlacedWords++;
        if (correctGroupMap[word] === parseInt(groupId)) {
          correctCount++;
        }
      });
    });
    
    // Calcola il punteggio percentuale
    // Se non sono state posizionate parole, assegna un punteggio di 0
    const percentageScore = totalWordsInGroups > 0 
      ? Math.round((correctCount / totalWordsInGroups) * 100) 
      : 0;
    
    // Genera i risultati dettagliati dei gruppi per la visualizzazione
    const groupResults = groups.map(group => {
      const groupId = group.id;
      const userSelectedWords = placedWords[groupId] || [];
      
      // Trova le parole corrette per questo gruppo
      const correctWordsForGroup = group.words;
      
      // Determina se tutti gli elementi nel gruppo sono stati selezionati correttamente
      const allCorrect = correctWordsForGroup.every(word => userSelectedWords.includes(word)) &&
                         userSelectedWords.every(word => correctWordsForGroup.includes(word));
      
      return {
        groupId: groupId,
        groupName: group.topic,
        userSelection: userSelectedWords,
        correctSelection: correctWordsForGroup,
        isCorrect: allCorrect
      };
    });
    
    // Log di debug per verificare i dati
    console.log(`SelectGroupGame [${gameData.chatId}] - Risultati calcolati:`, {
      score: percentageScore,
      correctAnswers: correctCount,
      totalQuestions: totalWordsInGroups
    });
    
    // Creiamo un oggetto risultato standardizzato con tutte le informazioni necessarie
    return {
      type: 'selectgroup',
      chatId: gameData.chatId,
      score: percentageScore,
      correctAnswers: correctCount,
      totalQuestions: totalWordsInGroups,
      totalWords: totalPlacedWords,
      completed: true, // Sempre true indipendentemente dal numero di parole posizionate
      completedAt: new Date().toISOString(),
      details: {
        gameType: 'selectgroup',
        correctWords: correctCount,
        totalWords: totalWordsInGroups,
        percentage: percentageScore,
        placedWords: totalPlacedWords,
        completed: true, // Assicuriamoci di avere il flag anche nei dettagli
        completedAt: new Date().toISOString()
      },
      // Ci assicuriamo di includere sempre i groupResults
      groupResults: groupResults,
      // Aggiungiamo questi campi per compatibilità con il formato atteso da InteractiveGamePage
      uniqueId: `selectgroup_${gameData.chatId}_${Date.now()}`,
      goToSummary: false
    };
  };
  
  // Verifica le risposte
  const handleCheckAnswers = () => {
    console.log(`SelectGroupGame [${gameData.chatId}] - handleCheckAnswers chiamata, isLastGame =`, isLastGame);
    
    // Imposta il flag di invio
    setIsSubmitting(true);
    
    // Calcola il punteggio e i risultati
    const result = calculateResults();
    
    // Pulizia del localStorage immediatamente dopo il calcolo del risultato
    console.log(`SelectGroupGame [${gameData.chatId}] - Avvio pulizia localStorage dopo completamento`);
    clearLocalStorageData();
    
    // Aggiorna i metadati per indicare che il gioco è stato completato
    const { metaKey } = getStorageKeys();
    if (metaKey) {
      try {
        const metaRaw = localStorage.getItem(metaKey);
        if (metaRaw) {
          const meta = JSON.parse(metaRaw);
          meta.completed = true;
          localStorage.setItem(metaKey, JSON.stringify(meta));
        }
      } catch (e) {
        console.error(`SelectGroupGame [${gameData.chatId}] - Errore aggiornamento metadati:`, e);
      }
    }
    
    // Se è l'ultimo gioco e l'utente sta consegnando, non mostriamo i risultati
    if (isLastGame) {
      console.log(`SelectGroupGame [${gameData.chatId}] - È l'ultimo gioco, chiamata onSubmitFinal`);
      
      // Prima chiama onComplete per essere sicuri che i risultati vengano salvati
      if (onComplete) {
        console.log(`SelectGroupGame [${gameData.chatId}] - Chiamata onComplete (ultimo gioco)`);
        onComplete(result);
        
        // Aggiungiamo una pulizia di backup con un delay per assicurarci che avvenga
        setTimeout(() => {
          console.log(`SelectGroupGame [${gameData.chatId}] - Tentativo pulizia ritardata`);
          clearLocalStorageData();
        }, 1000);
        
        // Poi chiama onSubmitFinal con un piccolo ritardo
        setTimeout(() => {
          if (onSubmitFinal) {
            console.log(`SelectGroupGame [${gameData.chatId}] - Chiamata onSubmitFinal`);
            onSubmitFinal();
          } else {
            console.error(`SelectGroupGame [${gameData.chatId}] - onSubmitFinal non definita`);
            setIsSubmitting(false);
          }
        }, 300);
      } else {
        console.error(`SelectGroupGame [${gameData.chatId}] - onComplete non definita`);
        setIsSubmitting(false);
      }
      
      return;
    }
    
    console.log(`SelectGroupGame [${gameData.chatId}] - Non è l'ultimo gioco, procedo normalmente`);
    
    if (onComplete) {
      console.log(`SelectGroupGame [${gameData.chatId}] - Chiamata onComplete (gioco normale)`);
      onComplete(result);
      
      // Aggiungiamo una pulizia di backup con un delay per assicurarci che avvenga
      setTimeout(() => {
        console.log(`SelectGroupGame [${gameData.chatId}] - Tentativo pulizia ritardata`);
        clearLocalStorageData();
      }, 1000);
    } else {
      console.error(`SelectGroupGame [${gameData.chatId}] - onComplete non definita`);
      setIsSubmitting(false);
    }
  };
  
  // Se non ci sono gruppi, mostra un messaggio
  if (!groups || groups.length === 0) {
    return (
      <div className="selectgroup-game">
        <h2>Nessun gruppo disponibile</h2>
      </div>
    );
  }
  
  // Rendering del gioco
  return (
    <div className="selectgroup-game">
      {/* Area delle parole disponibili */}
      <div 
        className="selectgroup-available-words"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDropToAvailable}
      >
        <h3>
          Parole disponibili
        </h3>
        <div className="selectgroup-words-container">
          {availableWords.map((word, index) => (
            <div
              key={`${word}-${index}`}
              className={`selectgroup-word-chip ${selectedWord === word && selectedWordSource === null ? 'selected' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, word)}
              onDragEnd={handleDragEnd}
              onClick={() => handleWordClick(word)}
            >
              {word}
            </div>
          ))}
        </div>
      </div>
      
      {/* Colonne per i gruppi */}
      <div className="selectgroup-columns">
        {groups.map(group => (
          <div 
            key={group.id} 
            className="selectgroup-column"
            onDragOver={(e) => handleDragOver(e, group.id)}
            onDrop={(e) => handleDrop(e, group.id)}
          >
            <h3>
              {group.topic}
            </h3>
            <div className="selectgroup-words-container">
              {placedWords[group.id] && placedWords[group.id].map((word, index) => (
                <div
                  key={`${word}-${index}`}
                  className={`selectgroup-word-chip placed ${selectedWord === word && selectedWordSource === group.id ? 'selected' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStartFromGroup(e, group.id, word)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => handlePlacedWordClick(e, group.id, word)}
                >
                  {word}
                  <span className="selectgroup-remove-icon">×</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Selettore di gruppo compatto (visibile solo quando una parola è selezionata) */}
      {showGroupSelector && selectedWord && (
        <div className="selectgroup-compact-selector">
          <div className="compact-selector-header">
            <span>Sposta "{selectedWord}" in:</span>
            <button 
              className="compact-selector-close" 
              onClick={handleCancelSelection}
            >
              ×
            </button>
          </div>
          <div className="compact-selector-groups">
            {groups.map(group => (
              <button 
                key={group.id}
                className="compact-selector-group-btn"
                onClick={() => handleAssignToGroup(group.id)}
              >
                {group.topic}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Overlay semplice per chiudere cliccando altrove */}
      {showGroupSelector && (
        <div 
          className="compact-selector-overlay" 
          onClick={handleCancelSelection}
        />
      )}
      
      <button 
        className={`selectgroup-button ${isLastGame ? 'submit-final' : ''}`}
        onClick={handleCheckAnswers}
        disabled={isSubmitting}
      >
        {isLastGame ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            Consegna
          </>
        ) : (
          <>
            Invia
          </>
        )}
      </button>
    </div>
  );
};

export default SelectGroupGame; 